import type React from "react";
import { useState } from "react";
import SendIcon from "@mui/icons-material/Send";
import { FormHelperText } from "@mui/material";
import { t } from "i18next";
import { z } from "zod";
import { MessageBoxContainer, MessageInput, SendButton } from "./Chat.styles";
import { useSendMessage } from "./hooks/useSendMessage";
import { customSendButtonByEntityType, type EntityType } from "./utils";

interface MessageBoxProps {
  isDisabled: boolean;
  entityType: EntityType;
  entityId: string;
  newMessage: string;
  secondRelatedEntityId?: string;
  setNewMessage: (message: string) => void;
  setShouldRefetch: (shouldRefetch: boolean) => void;
}
const messageSchema = z.object({
  message: z.string().min(1, "Message cannot be empty"),
});

const MessageBox = ({
  isDisabled,
  entityType,
  entityId,
  newMessage,
  secondRelatedEntityId,
  setNewMessage,
  setShouldRefetch,
}: MessageBoxProps) => {
  const [retrievedNotificationList, setRetrivedNotificationList] = useState<string[]>([]);
  const { onSubmit } = useSendMessage({
    newMessage,
    notifyList: retrievedNotificationList,
    entityType,
    entityId,
    secondRelatedEntityId,
    setNewMessage,
    setShouldRefetch,
  });
  const [isSendByEnterKey, setIsSentByEnterKey] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const CustomSendButtonComponent = customSendButtonByEntityType[entityType];

  const handleKeyDown: React.KeyboardEventHandler<HTMLInputElement> = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      if (CustomSendButtonComponent) {
        setIsSentByEnterKey(true);
      } else {
        onSubmit();
      }
    }
  };

  const handleValidateMessage = () => {
    const result = messageSchema.safeParse({ message: newMessage });
    const hasError = !result.success;
    if (!result.success) {
      const msg = result.error.formErrors.fieldErrors.message?.[0] ?? null;
      setError(msg);
      return { hasError };
    }

    setError(null);
    return { hasError };
  };
  return (
    <>
      <MessageBoxContainer>
        <MessageInput
          disabled={isDisabled}
          fullWidth
          multiline
          minRows={1}
          maxRows={100}
          placeholder={t("chat.message-box-placeholder")}
          value={newMessage}
          onChange={(e) => {
            setNewMessage(e.target.value);
            if (e.target.value) {
              setError(null);
            }
          }}
          onKeyDown={handleKeyDown}
        />

        {CustomSendButtonComponent ? (
          <CustomSendButtonComponent
            validateMessage={handleValidateMessage}
            onSubmit={onSubmit}
            isSendByEnterKey={isSendByEnterKey}
            setIsSentByEnterKey={setIsSentByEnterKey}
            setRetrivedNotificationList={setRetrivedNotificationList}
          />
        ) : (
          <SendButton color="primary" onClick={onSubmit} disabled={isDisabled}>
            <SendIcon />
          </SendButton>
        )}
        {error && (
          <FormHelperText
            sx={{
              ml: 0,
              fontWeight: 500,
              position: "absolute",
              left: 0,
              top: "calc(100% - 12px)",
              padding: "0 24px",
              fontSize: 12,
              marginY: "4px",
              height: "20px",
              visibility: error ? "visible" : "hidden",
              color: "error.main",
            }}
          >
            {error}
          </FormHelperText>
        )}
      </MessageBoxContainer>
    </>
  );
};

MessageBox.propTypes = {};

export default MessageBox;
